import {
	generateAccessName,
	AccessKey,
} from "../../../../../../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [AccessKey.ORDERS];
const ACCESS_SECTION_SEARCH_CAR: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.SEARCH_CAR,
];
const ACCESS_SECTION_SEARCH_SEARCH_ORDER: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.SEARCH_ORDER,
];
const ACCESS_SECTION_SEARCH_FILTER_ORDERS_BY_MULTY: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.FILTER_ORDERS_BY_MULTY,
];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	LIVE = AccessKey.LIVE_ORDERS,
	PRELIMINARY = AccessKey.PRELIMINARY_ORDERS,
	EXECUTING = AccessKey.EXECUTING_ORDERS,
	ALL = AccessKey.ALL_ORDERS,
	CLOSED = AccessKey.CLOSED_ORDERS,
	//
	SEARCH_CAR = AccessKey.SEARCH_CAR,
	SEARCH_EXECUTOR = AccessKey.SEARCH_EXECUTOR,
	SEARCH_ORDER = AccessKey.SEARCH_ORDER,
	SHOW_ONLY_MY_ORDERS = AccessKey.SHOW_ONLY_MY_ORDERS,
	SHOW_ORDERS_WITHOUT_EXECUTORS = AccessKey.SHOW_ORDERS_WITHOUT_EXECUTORS,
	FILTER_ORDERS_BY_MULTY = AccessKey.FILTER_ORDERS_BY_MULTY,
	GPS_SPOOFING = AccessKey.GPS_SPOOFING,
	//
	FLEET_NUMBER = AccessKey.FLEET_NUMBER,
	LINCENSE_PLATE = AccessKey.LINCENSE_PLATE,
	//
	PHONE = AccessKey.PHONE,
	FROM = AccessKey.FROM,
	TO = AccessKey.TO,
	//
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	CAR_CLASSES = AccessKey.CAR_CLASSES,
	SERVICES = AccessKey.SERVICES,
}

// SEARCH_CAR;
// SEARCH_ORDER;
// FILTER_ORDERS_BY_MULTY;

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];
const generateAccessSectionSearchCar = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_SEARCH_CAR,
	key,
];
const generateAccessSectionSearchOrder = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_SEARCH_SEARCH_ORDER,
	key,
];
const generateAccessSectionFilterOrdersByMulty = (
	key: AccessKey,
): AccessKey[] => [...ACCESS_SECTION_SEARCH_FILTER_ORDERS_BY_MULTY, key];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	//* tabs group
	[TabKeys.LIVE]: generateAccessSection(AccessKey.LIVE_ORDERS),
	[TabKeys.PRELIMINARY]: generateAccessSection(AccessKey.PRELIMINARY_ORDERS),
	[TabKeys.EXECUTING]: generateAccessSection(AccessKey.EXECUTING_ORDERS),
	[TabKeys.ALL]: generateAccessSection(AccessKey.ALL_ORDERS),
	[TabKeys.CLOSED]: generateAccessSection(AccessKey.CLOSED_ORDERS),
	//* saerch and filter group
	[TabKeys.SEARCH_CAR]: generateAccessSection(AccessKey.SEARCH_CAR),
	[TabKeys.SEARCH_EXECUTOR]: generateAccessSection(AccessKey.SEARCH_EXECUTOR),
	[TabKeys.SEARCH_ORDER]: generateAccessSection(AccessKey.SEARCH_ORDER),
	[TabKeys.SHOW_ONLY_MY_ORDERS]: generateAccessSection(
		AccessKey.SHOW_ONLY_MY_ORDERS,
	),
	[TabKeys.SHOW_ORDERS_WITHOUT_EXECUTORS]: generateAccessSection(
		AccessKey.SHOW_ORDERS_WITHOUT_EXECUTORS,
	),
	[TabKeys.FILTER_ORDERS_BY_MULTY]: generateAccessSection(
		AccessKey.FILTER_ORDERS_BY_MULTY,
	),
	[TabKeys.GPS_SPOOFING]: generateAccessSection(AccessKey.GPS_SPOOFING),
	//
	[TabKeys.FLEET_NUMBER]: generateAccessSectionSearchCar(
		AccessKey.FLEET_NUMBER,
	),
	[TabKeys.LINCENSE_PLATE]: generateAccessSectionSearchCar(
		AccessKey.LINCENSE_PLATE,
	),
	//
	[TabKeys.PHONE]: generateAccessSectionSearchOrder(AccessKey.PHONE),
	[TabKeys.FROM]: generateAccessSectionSearchOrder(AccessKey.FROM),
	[TabKeys.TO]: generateAccessSectionSearchOrder(AccessKey.TO),
	//
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]:
		generateAccessSectionFilterOrdersByMulty(
			AccessKey.COMPANIES_AND_TAXI_SERVICES,
		),
	[TabKeys.CAR_CLASSES]: generateAccessSectionFilterOrdersByMulty(
		AccessKey.CAR_CLASSES,
	),
	[TabKeys.SERVICES]: generateAccessSectionFilterOrdersByMulty(
		AccessKey.SERVICES,
	),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

const AccessKeysOrderTabs = [
	TabAccessNames[TabKeys.LIVE],
	TabAccessNames[TabKeys.PRELIMINARY],
	TabAccessNames[TabKeys.EXECUTING],
	TabAccessNames[TabKeys.ALL],
	TabAccessNames[TabKeys.CLOSED],
];

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY, AccessKeysOrderTabs };
